import { parsePaymentsValidationError } from '@/api'
import { Trans, useLingui } from '@lingui/react/macro'
import axios from 'axios'
import { ExternalLink } from 'lucide-react'
import { useState } from 'react'
import { mintRedemptionLink } from '../api/mintRedemptionLink'

// IV-31 — the redemption URL is PERMANENT, UNREVOCABLE BEARER AUTHORITY. It must never enter the
// DOM (href/src/value/title/aria-label/data-*), React state, a log, a toast, the clipboard,
// analytics or Sentry. Third-party SDKs read the DOM, and Google Tag Manager is configured outside
// this repository, so no scrubber here can cover it: avoidance is the only complete control.
// This is a <button>, never an <a href>. Do NOT pattern-match PayoutClaimCta — its href is a
// static constant that has never carried per-user data.

type Refusal =
  | 'POPUP_BLOCKED'
  | 'TREMENDOUS_PAYOUT_REFUNDED'
  | 'TREMENDOUS_REWARD_NOT_ISSUED'
  | 'TREMENDOUS_REWARD_EXPIRED'
  | 'TREMENDOUS_NOT_A_REWARD_PAYOUT'
  | 'TREMENDOUS_COLLECT_UNAVAILABLE'
  | 'NOT_FOUND'
  | 'GENERIC'

const REFUSAL_CODES: ReadonlySet<string> = new Set<Refusal>([
  'TREMENDOUS_PAYOUT_REFUNDED',
  'TREMENDOUS_REWARD_NOT_ISSUED',
  'TREMENDOUS_REWARD_EXPIRED',
  'TREMENDOUS_NOT_A_REWARD_PAYOUT',
  'TREMENDOUS_COLLECT_UNAVAILABLE'
])

// Anything not specifically recognised (401/409/500/502/503, network failure, …) gets the
// reassuring generic copy — the reward is not affected by a failed mint.
const classifyRefusal = (err: unknown): Refusal => {
  const code = parsePaymentsValidationError(err)?.formErrorCode
  if (code != null && REFUSAL_CODES.has(code)) return code as Refusal
  if (axios.isAxiosError(err) && err.response?.status === 404) return 'NOT_FOUND'
  return 'GENERIC'
}

type PayoutCollectCtaProps = {
  payoutId: string
}

export const PayoutCollectCta = ({ payoutId }: PayoutCollectCtaProps) => {
  const { t } = useLingui()
  // Holds only which refusal to show — never the URL.
  const [refusal, setRefusal] = useState<Refusal | null>(null)
  const [isCollecting, setIsCollecting] = useState(false)

  const refusalCopy = (kind: Refusal): string => {
    switch (kind) {
      case 'POPUP_BLOCKED':
        return t`Your browser blocked the popup. Allow popups for this site and try again.`
      case 'TREMENDOUS_PAYOUT_REFUNDED':
        return t`This payout was returned to your balance — there's nothing to collect.`
      case 'TREMENDOUS_REWARD_NOT_ISSUED':
        return t`Your reward isn't ready yet. Check back shortly.`
      case 'TREMENDOUS_REWARD_EXPIRED':
        return t`This reward has expired and can no longer be collected.`
      case 'TREMENDOUS_NOT_A_REWARD_PAYOUT':
        return t`This payout can't be collected here.`
      // Operational, not a statement about the reward: the server evaluates it first, so a payout
      // on another rail also answers it while Tremendous is switched off.
      case 'TREMENDOUS_COLLECT_UNAVAILABLE':
        return t`Reward collection is temporarily unavailable. Try again shortly.`
      case 'NOT_FOUND':
        return t`We couldn't find this payout.`
      case 'GENERIC':
        return t`We couldn't open your reward. It's safe — please try again.`
    }
  }

  const onCollect = async () => {
    if (isCollecting) return
    setRefusal(null)
    // SYNCHRONOUS, inside the gesture, before any await — or Safari and Firefox block it (IV2).
    // NO 'noopener': with that feature window.open returns null BY SPEC and there is no handle.
    const tab = window.open('', '_blank')
    if (!tab) {
      // Popup blocked. Do NOT call the endpoint (IV1): minting a reward nobody receives creates
      // an unrevocable credential for nothing.
      setRefusal('POPUP_BLOCKED')
      return
    }
    tab.opener = null // sever the opener; we dropped 'noopener' to keep the handle
    setIsCollecting(true)
    try {
      const url = await mintRedemptionLink(payoutId)
      tab.location.replace(url)
    } catch (e) {
      tab.close()
      setRefusal(classifyRefusal(e))
    } finally {
      setIsCollecting(false)
    }
  }

  return (
    <>
      <button
        type="button"
        className="text-body-3 text-orange inline-flex w-fit items-center gap-1 font-medium hover:underline disabled:opacity-60"
        onClick={onCollect}
        disabled={isCollecting}
      >
        <Trans>Collect on Tremendous</Trans>
        <ExternalLink size={14} />
      </button>
      {refusal && (
        <span className="text-body-3 text-gray-90 block" role="status">
          {refusalCopy(refusal)}
        </span>
      )}
    </>
  )
}
